import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useDataTableContext } from 'components/DataTable'
import { queryClient } from 'services/queryClient'
import useApiService from 'services/api'
import { ContactResponse } from 'models/contacts'

interface Props {
  data: ContactResponse[]
  tableQueryKey: string[]
  filterParams: Record<string, unknown>
  forCompanyId?: number
  forDoorProxyId?: number
  campaignId?: number
}

/**
 * Hook that manages bulk contact deletion state for the ContactsTable.
 *
 * Derives the page-visible contact IDs relevant to the current selection
 * and the effective display count, handles the delete API call, and
 * invalidates relevant query caches (contacts list, company detail,
 * door proxy, campaigns) after a successful deletion.
 *
 * @returns Modal visibility state, handlers, page-visible IDs, selection
 *   mode, and the effective contact count.
 */
export function useBulkContactsDeleter(props: Props) {
  const api = useApiService()
  const [showModal, setShowModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    state: { rowSelection, isAllRowsSelected, totalRowsInBackend },
  } = useDataTableContext()

  /**
   * When `isAllRowsSelected` is false, `pageVisibleIds` contains the IDs
   * the user explicitly checked (sent as `include_ids`). When true, it
   * contains the IDs they unchecked (sent as `exclude_ids`). The count
   * reflects the total rows the operation will affect across all pages.
   */
  const { pageVisibleIds, count } = useMemo(() => {
    const ids: number[] = []
    let unselectedCount = 0
    props.data?.forEach((row) => {
      // `rowSelection` is keyed by stringified row ID (set via `getRowId`),
      // matching `isContactSelected` in ContactsTable. Indexing by array
      // position never matches, so every loaded row read as unselected.
      const selected = !!rowSelection?.[String(row.id)]
      if (isAllRowsSelected ? !selected : selected) ids.push(row.id)
      if (!selected) unselectedCount++
    })
    const count = isAllRowsSelected
      ? (totalRowsInBackend ?? 0) - unselectedCount
      : ids.length
    return { pageVisibleIds: ids, count }
  }, [rowSelection, isAllRowsSelected, props.data, totalRowsInBackend])

  const openModal = () => setShowModal(true)
  const closeModal = () => setShowModal(false)

  const confirmDelete = async () => {
    setIsDeleting(true)
    try {
      const data = await api.bulkDeleteContacts(
        pageVisibleIds,
        isAllRowsSelected,
        props.filterParams
      )

      if (data?.message) {
        toast.info(data.message)
      } else {
        toast.success('Contacts deleted successfully')
      }

      const invalidations: Promise<unknown>[] = [
        queryClient.refetchQueries({ queryKey: props.tableQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        // Recipient counts in any open sequence wizard derive from
        // lead-review; deleted contacts must drop out of those counts
        // before launch (FIR-7242).
        queryClient.invalidateQueries({ queryKey: ['campaign-lead-review'] }),
      ]
      if (props.forCompanyId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: ['company', props.forCompanyId],
          })
        )
      }
      if (props.forDoorProxyId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: ['door_proxy', props.forDoorProxyId],
          })
        )
      }
      if (props.campaignId) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ['campaigns'] })
        )
      }
      await Promise.all(invalidations)

      setShowModal(false)
    } catch {
      toast.error('Failed to delete contacts. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  return {
    showModal,
    openModal,
    closeModal,
    confirmDelete,
    isDeleting,
    pageVisibleIds,
    isAllRowsSelected,
    count,
  }
}
